import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import { get, find } from 'lodash';
import {
	SdfAlert,
	SdfBox,
	SdfBusyIndicator,
	SdfButton,
	SdfCheckbox,
	SdfDatePicker,
	SdfExpandableBox,
	SdfFocusPane,
	SdfIcon,
	SdfLink,
	SdfSpotIllustration,
	SdfSwitch
} from '@synerg/react-components';
import { showErrorModal, useApplicationState } from '../../utilities/applicationState';

const REFER_PROJECT_DOC = 'https://developers.adp.com/articles/guides/partner-development-learning-guide';
const EMPLOYMENT_VERIFICATION_DOC = 'https://developers.adp.com/articles/guides/adp-smartcompliance-employment-verification-api-specification';

export const ManageDevelopmentProject = (props = {}) => {
	const { projectDetail, isDataCloudUser } = props || {};
	const [projectData, setProjectData] = useState(projectDetail);
	const [milestones, setMilestones] = useState(projectDetail.milestones);
	const [isModalVisible, setModalVisibility] = useState(false);
	const [isError, setError] = useState(false);
	const [showToast, setShowToast] = useState(false);
	const [loading, setLoading] = useState(false);
	const [infoMessage, setInfoMessage] = useState(null);
	const [enableGPP, setEnableGPP] = useState(false);
	const [disableMilestoneToggle, setDisableMilestoneToggle] = useState(false);
	const [popupContent, setPopupContent] = useState({});

	const [, dispatch] = useApplicationState();
	const history = useHistory();

	useEffect(() => {
		const startDateFields = document.querySelectorAll(`[id^='milestone-start-date_']`);
		const endDateFields = document.querySelectorAll(`[id^='milestone-end-date_']`);

		const scopeFields = document.querySelectorAll(`[id^='toggle-milestone_']`);
		const completeFields = document.querySelectorAll(`[id^='is-completed_']`);

		if (startDateFields) {
			startDateFields.forEach((element) => {
				element.addEventListener('sdfChange', startDateChangeHandler);
			});
		}

		if (endDateFields) {
			endDateFields.forEach((element) => {
				element.addEventListener('sdfChange', endDateChangeHandler);
			});
		}

		if (scopeFields) {
			scopeFields.forEach((element) => {
				element.addEventListener('sdfChange', switchChangeHandler);
			});
		}

		if (completeFields) {
			completeFields.forEach((element) => {
				element.addEventListener('sdfChange', completedChangeHandler);
			});
		}

		const projectStartDateField = document.querySelector('#project-start-date');
		if (projectStartDateField)
			projectStartDateField.addEventListener('sdfChange', projectStartDateChangeHandler);

		setDateFieldRules(milestones);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (Array.isArray(milestonesFilter()) && milestonesFilter().length > 0) {
			const isAllCompleted = milestonesFilter().filter((item) => item.scope === true).find((item) => item.complete === false || item.complete === undefined);
			// to disable last active milestone toggle
			const scopedMilestone = milestonesFilter().filter((item) => item.scope === true);
			if (scopedMilestone.length === 1) setDisableMilestoneToggle(true);
			else { setDisableMilestoneToggle(false); }

			if (isAllCompleted || !projectData.startDate || projectData.associatedProject) { setEnableGPP(false); return; }
			else {
				setEnableGPP(true);
				setPopupContent({
					title: 'Before generating production project...', step: 'CONFIRMATION', modalSize: 'md'
				});
				return;
			}
		}
		setEnableGPP(false);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [milestones, projectData])

	const setDateFieldRules = (milestonesData) => {
		if (Array.isArray(milestonesData) && milestonesData.length > 0) {
			const tmpMilestones = milestonesData.map((item) => ({
				...item,
				endDateRules: item.startDate
				?
					[{
						type: "DISABLED",
						startDateInclusive: new Date(0, 0, 0),
						endDateInclusive: new Date(item.startDate).setDate(
							new Date(item.startDate).getDate()
						)
					}]
				: [],
				startDateRules: item.endDate
				?
					[{
						type: 'ENABLED',
						startDateInclusive: new Date(0, 0, 0),
						endDateInclusive: stringToDate(item.endDate)
					}]
				: [],
			}));
			setMilestones(tmpMilestones);
		} else setMilestones(milestonesData);
	};

	const getLastModifiedText = (history = [], process = '') => {
		if (history.length === 0) return '';
		const selectedItem = history.find((item) => item.key === process);
		if (!selectedItem) return '';
		return `Last modified by ${selectedItem.user.name} on ${getDate(
			selectedItem.date
		)} at ${getTime(selectedItem.date)} ET`;
	};

	const getDate = (dateStr) => {
		const date = new Date(dateStr);
		return ((date.getMonth() + 1) + '/' + date.getDate() + '/' + date.getFullYear());
	};

	const getRequestPayloadDate = (dateStr) => {
		const dateArr = dateStr.split('-');
		return dateArr[1] + '/' + dateArr[2]+ '/'+ dateArr[0];
	};

	const getInputDate = (value = '') => {
		if (typeof value === 'string') return value.replace(/Z/g, '');
    	else if (value) return new Date(value).toDateString();
		else return value;
	};

	const getTime = (dateStr) => {
		const date = new Date(dateStr);
		const hour = date.getHours() % 12 || 12;
		const time = hour + ':' + date.getMinutes();
		const amOrPm = date.getHours() >= 12 ? 'pm' : 'am';

		return time + ' ' + amOrPm;
	};

	const switchChangeHandler = (event) => {
		const id = event.target.id.split('_')[1];
    updateData(false, { scope: event.target.checked }, id);
	};

	const stringToDate = (isoDate) => {
		if (!isoDate) {
			return null;
		}
		const parts = isoDate.split('-');
		return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
	};

	const projectStartDateChangeHandler = (ev) => {
		updateData(true, { startDate: getRequestPayloadDate(ev.detail) });
	};

	const startDateChangeHandler = (ev) => {
		const startDate = stringToDate(ev.detail);
		const id = ev.target.id.split('_')[1];

		if (!id || !startDate) return;

		const tmpMilestones = [...milestones];
		tmpMilestones[id].startDate = startDate;

		if (startDate) {
			tmpMilestones[id].endDateRules = [{
				type: 'DISABLED',
				startDateInclusive: new Date(0, 0, 0),
				endDateInclusive: startDate.setDate(startDate.getDate() - 1)
			}];
			updateData(false, { startDate: getRequestPayloadDate(ev.detail) }, id);
		} else {
			tmpMilestones[id].endDateRules = [];
		}
		setMilestones(tmpMilestones);
	};

	const endDateChangeHandler = (ev) => {
		const endDate = ev.detail;
		const id = ev.target.id.split('_')[1];

		if (!id || !endDate) return;

		const tmpMilestones = [...milestones];
		tmpMilestones[id].endDate = endDate;

		if (endDate) {
			tmpMilestones[id].startDateRules = [{
				type: 'ENABLED',
				startDateInclusive: new Date(0, 0, 0),
				endDateInclusive: stringToDate(endDate)
			}];
			updateData(false, { endDate: getRequestPayloadDate(endDate) }, id);
		} else {
			tmpMilestones[id].startDateRules = [];
		}
		setMilestones(tmpMilestones);
	};

	const completedChangeHandler = (event) => {
		const id = event.target.id.split('_')[1];
		updateData(false, { complete: event.target.checked }, id);
	};

	const isCompletedDisabled = (milestone) => {
		if (milestone.startDate && milestone.endDate && milestone.scope && !projectData.associatedProject)
      return false;
    else return true;
	};

  const isToggleMilestoneDisabled = (milestone) => {
    let flag = undefined;
    if (projectData.associatedProject) flag = true;
    if (milestone.complete === true) flag = true;
    if (disableMilestoneToggle && milestone.scope === true) flag = true;
    return flag;
  };

	const Icon = <SdfIcon icon='alert-success' className='pr-2 color-success' />;

	const updateData = async (projectChange = false, payload = {}, milestoneIndex) => {
		setLoading(true);
		setShowToast(false);

		let URL = `${import.meta.env.VITE_ROOT_PATH}/api/milestone-changes/${projectDetail._id
			}/${parseInt(milestoneIndex) + 1}`;

		if (projectChange)
			URL = `${import.meta.env.VITE_ROOT_PATH}/api/project/start-date/${projectDetail._id}`;

		try {
			const { data } = await axios.put(URL, payload);
			if (projectChange === false) { setDateFieldRules(data); }
			else {
				const tmpProjectData = {...projectData};
				tmpProjectData['startDate'] = data.startDate;
				setProjectData(tmpProjectData);
			}
			setLoading(false);
			setShowToast(true);
			setError(false);
			setInfoMessage('Milestone updated successfully!');
		} catch (e) {
			const errorMessage = get(e,'response.data.message',
				'Something went wrong when trying to update milestone. Please try again.'
			);
			setInfoMessage(errorMessage);
			if (errorMessage.toLowerCase().includes('access denied')) {
				history.push('/access-denied?error=unauthorized');
			} else {
				dispatch(showErrorModal(errorMessage));
				setLoading(false);
				setShowToast(true);
				setError(true);
			}
		}
	};

	const openGeneratePopup = () => {
		setModalVisibility(true);
		setPopupContent({
			title: 'Before generating production project...', step: 'CONFIRMATION', modalSize: 'md'
		});
	};

	const generateProductionProject = async () => {
		setLoading(true);
		setShowToast(false);
		const URL = `${import.meta.env.VITE_ROOT_PATH}/api/project/${projectDetail._id}/promote`;
		try {
			const { data } = await axios.post(URL);
      const tmpProjectData = { ...projectData };
      tmpProjectData['associatedProject'] = data;
      setProjectData(tmpProjectData);
			setLoading(false);
			setShowToast(true);
			setError(false);
			setInfoMessage('Production Project created successfully!');
			setPopupContent({ title: 'Generate Production Project', step: 'SUCCESS', modalSize: 'md' });
		} catch (e) {
			const errorMessage = get(e, 'response.data.message',
				'Sorry, something went wrong when trying to create the Production Project. Please try again.'
			);
			setInfoMessage(errorMessage);
			if (errorMessage.toLowerCase().includes('access denied')) {
				history.push('/access-denied?error=unauthorized');
			} else {
				dispatch(showErrorModal(errorMessage));
				setLoading(false);
				setShowToast(true);
				setError(true);
			}
		}
	};

	const milestonesFilter = () => {
		if (isDataCloudUser) {
			const val = find(milestones, { name: 'ADP API Integration' });
			return val ? [val] : [];
		} else {
			return milestones || [];
		}
	};

	return (
		<div className='row mt-8'>
			{loading ? (
				<SdfBusyIndicator
					style={{ color: '#1a8099' }}
					size='xl'
					fullScreen={true}
					overlay={true}
				/>
			) : null}

			<SdfAlert
				className='fixed min-w-1/3'
				style={{ top: '6rem', right: 'calc(100% - 66%)', zIndex: '9999' }}
				hidden={!showToast}
				autoClose={true}
				autoCloseAfter={3000}
				closeable={true}
				useAnimation={true}
				status={isError ? 'error' : 'success'}
			>
				{infoMessage}
			</SdfAlert>

			<div className='pr-10 mt-8'>
				<SdfAlert status='warning' type='banner' size='md' className='max-w-3/4'>
					<span slot='title'>Before you begin</span>
					<span className='block'>
						{ !isDataCloudUser ? 'Please read through our Partner Development Learning Guide. This will assist in establishing your milestone dates below.' : 'Please read through our ADP® DataCloud Identity, Income and Employmment Verification API guide.' }
					</span>
					<SdfButton
						className='mt-4'
						onClick={() =>
							window.open(
								!isDataCloudUser ? REFER_PROJECT_DOC : EMPLOYMENT_VERIFICATION_DOC, '_blank'
							)
						}
					>
						View learning guide
					</SdfButton>
				</SdfAlert>
			</div>

			<div className='text-heading-06 font-bold mt-10'>
				Development Milestones
			</div>

			<div className='text-body-02 mt-6'>
				Expand to enter tentative dates for each of in-scope milestones.
				{ !isDataCloudUser && <span>
				  &nbsp;For more information, refer to the&nbsp;
					<SdfLink
						href='https://developers.adp.com/articles/guides/developer-self-service-portal-step-by-step-integration-guide?chapter=1'
						target='_blank'
					>
						step-by-step implementation documentation
					</SdfLink>
					&nbsp;on ADP Developer Resources.
				</span> }
			</div>

			<div className='text-heading-04 font-bold mt-8'>
				Toggle off any milestones that do not apply to the scope of your
				integration project.
			</div>

			<div className='flex mt-10 gap-8'>
				<div className='flex-1'>
					<SdfExpandableBox triggerPlacement='after' variant='bordered'>
						<div slot='title' className='flex'>
							<div className='text-heading-03 font-bold pl-12'>
								Project Start Date
							</div>
						</div>

						<div className='pt-8 pb-8 pl-12 pr-8'>
							<div className='text-body-03'>
								Please provide your projected development start date.
							</div>
							<div className='p-6'>
								<SdfDatePicker
									id={'project-start-date'}
									label='Projected Start Date'
									className='w-6/12 min-w-sm'
                  disabled={projectData.associatedProject ? true : undefined}
									value={getInputDate(projectData.startDate)}
									onBlur={startDateChangeHandler}
								/>
							</div>
						</div>
					</SdfExpandableBox>

					{milestonesFilter().map((milestone, index ) => (
						<React.Fragment key={!isDataCloudUser ? index : (milestone.id-1)}>
							<SdfExpandableBox
								triggerPlacement='after'
								variant='bordered'
								className='mt-5'
							>
								<div slot='header' className='flex items-center'>
									<div>
										<SdfSwitch
                      className={ isToggleMilestoneDisabled(milestone) ? 'cursor-not-allowed': ''}
											id={`toggle-milestone_${!isDataCloudUser ? index : (milestone.id-1)}`}
											checked={milestone.scope}
											disabled={isToggleMilestoneDisabled(milestone)}
											data-sdf-squash=''
										/>
									</div>
									<div className='text-heading-03 font-bold flex-1 capitalize'>
										{milestone.name}
									</div>
									<div className='text-sm pr-2'>
										{milestone.complete === true ? (
											<div>
												{Icon}
												{getLastModifiedText(milestone.history, 'COMPLETE')}
											</div>
										) : null}
									</div>
								</div>

								<div className={milestone.complete ? 'bg-alert-neutral-50 mt-4' : 'mt-4' }>
									<div className='pt-4 pb-8 pl-12 pr-8'>
										<div className='text-body-03'>{milestone.description}</div>
										<div className='inline-flex w-full'>
											<div className='text-heading-02 font-bold mt-4 flex-1'>
												Suggested duration: {milestone.duration} days
											</div>
											{milestone.complete === true ? (
												<div className='text-body-02 mt-4 color-success'>
													{Icon} Milestone Complete
												</div>
											) : null}
										</div>
										<div className='inline-flex mt-6 gap-8'>
											<div className=''>
												<SdfDatePicker
													id={`milestone-start-date_${!isDataCloudUser ? index : (milestone.id-1)}`}
													label='Projected Start Date'
													className={`w-full min-w-sm ${milestone.complete || !milestone.scope ? 'cursor-not-allowed': ''}`}
													value={getInputDate(milestone.startDate)}
													placeholder='mm/dd/yyyy'
													disabled={milestone.complete || !milestone.scope}
													rules={milestone.startDateRules}
												/>
												<div className='text-sm mt-2'>
													{getLastModifiedText(milestone.history, 'STARTDATE')}
												</div>
											</div>
											<div className=''>
												<SdfDatePicker
													id={`milestone-end-date_${!isDataCloudUser ? index : (milestone.id-1)}`}
													label='Projected End Date'
													className={`w-full min-w-sm ${milestone.complete || !milestone.scope ? 'cursor-not-allowed': ''}`}
													value={getInputDate(milestone.endDate)}
													placeholder='mm/dd/yyyy'
													disabled={milestone.complete || !milestone.scope}
													rules={milestone.endDateRules}
												/>
												<div className='text-sm mt-2'>
													{getLastModifiedText(milestone.history, 'ENDDATE')}
												</div>
											</div>
										</div>
										<SdfBox
											variant='filled'
											spacing='default'
											className={milestone.complete ? 'mt-10 bg-alert-neutral-200': 'mt-10'}
										>
											<div className='text-heading-03 font-bold'>
												Milestone complete?
											</div>
											<div className='mt-4'>
												<SdfCheckbox
													id={`is-completed_${!isDataCloudUser ? index : (milestone.id-1)}`}
													checked={milestone.complete}
													disabled={
														isCompletedDisabled(milestone) === true ? true : undefined
													}
													label='I finished the necessary development to complete this milestone'
												/>
											</div>
										</SdfBox>
										<div className='text-sm mt-2'>
											{getLastModifiedText(milestone.history, 'COMPLETE')}
										</div>
									</div>
								</div>
							</SdfExpandableBox>
						</React.Fragment>
					))}
				</div>
				<div className='max-w-1/4'>
					<SdfBox variant='bordered' spacing='loose' className='text-center'>
						<div className=''>
							<SdfSpotIllustration illustration-name='browser-pointer' />
						</div>
						<div className='text-heading-05 font-bold'>
							Ready to take the next step?
						</div>
						<div className='text-body-02 mt-2'>
							When your development is complete for all in-scope milestones,
							please generate a production project with new credentials. To
							allow for future editing of your app, we will keep this copy to
							use as your development project. Please finish all in-scope
							milestones before generating a production project.
						</div>
						<SdfButton
							className='mt-3'
							disabled={enableGPP ? undefined : true}
							onClick={openGeneratePopup}
						>
							Generate production project
						</SdfButton>
					</SdfBox>
				</div>
			</div>

			<SdfFocusPane
				visible={isModalVisible}
				closeable={true}
				dismissOnClickOutside={true}
				heading={popupContent.title}
				hideDismissButton={true}
				hideAcceptButton={true}
				onSdfDismiss={() => {
					setModalVisibility(false);
				}}
				spacing={'loose'}
				size={popupContent.modalSize}
			>
				{loading ? (
					<SdfBusyIndicator
						style={{ color: '#1a8099' }}
						size='xl'
						fullScreen={true}
						overlay={true}
					/>
				) : null}

				{/* confirmation box */}
				{popupContent.step === 'CONFIRMATION' ? (
					<React.Fragment>
						<div className='mt-6 ml-6 mr-6'>
							<div className='text-heading-03 font-bold mt-4 mb-6'>
								Have you demoed your integration to your technical advisor and
								passed security review?
							</div>
						</div>

						<SdfButton
							slot='custom-buttons'
							onClick={() =>
								setPopupContent({
									title: 'Before generating production project...',
									step: 'NO-CONFIRMATION',
									modalSize: 'md'
								})
							}
						>
							No I Haven't.
						</SdfButton>
						<SdfButton
							slot='custom-buttons'
							emphasis='primary'
							onClick={() =>
								setPopupContent({ title: 'Are you sure?', step: 'GENERATE-CONFIRMATION', modalSize: 'lg' })
							}
						>
							Yes, I Have.
						</SdfButton>
					</React.Fragment>
				) : null }

				{/* No confirmation box */}
				{popupContent.step === 'NO-CONFIRMATION' ? (
					<React.Fragment>
						<div className='mt-6 ml-6 mr-6'>
							<div className='text-heading-03 font-bold mt-4 mb-6'>
								Please demo your integration to your technical advisor and they
								will help you submit to security review.
							</div>
							<div className='text-body-02 mt-4 mb-6'>
								Please contact your advisor by filling out the form on the
								bottom right of the screen.
							</div>
						</div>

						<SdfButton
							slot='custom-buttons'
							onClick={() =>
								setPopupContent({ title: 'Are you sure?', step: 'GENERATE-CONFIRMATION', modalSize: 'lg' })
							}
						>
							Cancel
						</SdfButton>
					</React.Fragment>
				) : null}

				{/* Generate Confirmation  */}
				{popupContent.step === 'GENERATE-CONFIRMATION' ? (
					<React.Fragment>
						<div className='mt-6 ml-24 mr-16'>
							<div className='ml-8'>
								<div className='w-full'>
									<SdfSpotIllustration illustration-name='gear-arrows'size='lg' />
								</div>
								<div className='text-heading-06 font-bold mt-2'>
									Generate production project for <br /> {projectDetail.name}
								</div>
								<div className='w-full mt-5'>
									<SdfAlert status={'info'} size='lg'>
										Note: You can always come back to this project for
										development testing purposes.
									</SdfAlert>
								</div>
								<div className='text-body-02 mt-4 mb-6'>
									Your production project will be generated after you confirm.
									By generating the production project, you will be able to
									configure an app listing in production, configure integration
									with production credentials, and conduct live subscription
									testing.
								</div>
							</div>
						</div>

						<SdfButton slot='custom-buttons' onClick={() => setModalVisibility(false)}>
							Cancel
						</SdfButton>
						<SdfButton slot='custom-buttons' emphasis='primary' onClick={generateProductionProject}>
							Confirm generation
						</SdfButton>
					</React.Fragment>
				) : null}

				{/* Success */}
				{popupContent.step === 'SUCCESS' ? (
					<React.Fragment>
						<div className='mt-6 ml-24 mr-16'>
							<div className='ml-8'>
								<div className='w-full'>
									<SdfSpotIllustration
										illustration-name='checkmark'
										size='lg'
									/>
								</div>
								<div className='text-heading-06 font-bold mt-2'>
									{projectDetail.name} Generated!
								</div>
								<div className='text-body-02 mt-4 mb-6'>
									Now you can proceed to the Production project to continue
									production configuration.
								</div>
							</div>
						</div>

						<SdfButton slot='custom-buttons' onClick={() => {
							setModalVisibility(false);
							window.location.reload();
						}}>
							Done
						</SdfButton>
						<SdfButton
							slot='custom-buttons'
							emphasis='primary'
							onClick={() => {
								if (projectData.associatedProject._id) {
									history.push(`/projects/${projectData.associatedProject._id}`);
								}
								window.location.reload();
							}}
						>
							View Production Project
						</SdfButton>
					</React.Fragment>
				) : null}
			</SdfFocusPane>
		</div>
	);
};
